import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { UserRole } from 'types';
import { INVITEE_NAME_MAX_LENGTH } from 'utils';
import { Input, RadioButton } from 'components';
import { CopyIcon } from 'components/Icon';

import styles from './InviteToStage.GenerateLink.module.scss';

export type GenerateLinkProps = {
  defaultRole: UserRole.SPEAKER | UserRole.MODERATOR;
  onGenerate?(inviteeName: string, role: UserRole.SPEAKER | UserRole.MODERATOR): void;
};

export type FormValues = {
  name: string;
  role: UserRole.MODERATOR | UserRole.SPEAKER;
};

export const validationSchema = yup.object().shape({
  name: yup
    .string()
    .max(INVITEE_NAME_MAX_LENGTH, `must be less than ${INVITEE_NAME_MAX_LENGTH} characters`)
    .required('Invitee name is required'),
  role: yup.string()
});

const roleOptions: Array<{ label: string; value: UserRole.SPEAKER | UserRole.MODERATOR }> = [
  { label: 'Speaker', value: UserRole.SPEAKER },
  { label: 'Moderator', value: UserRole.MODERATOR }
];

const GenerateLink = ({ defaultRole = UserRole.SPEAKER, onGenerate }: GenerateLinkProps) => {
  const form = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: '',
      role: defaultRole
    }
  });

  const onSubmit = (validValues: FormValues) => {
    onGenerate?.(validValues.name, validValues.role);
  };

  return (
    <form className={styles.container} onSubmit={form.handleSubmit(onSubmit)}>
      <div className={styles.name}>
        <Controller
          name="name"
          control={form.control}
          render={({ field }) => (
            <Input
              label="Invitee name"
              id="invite_name"
              placeholder="Enter name"
              invalid={!!form.formState.errors.name}
              feedback={form.formState.errors.name?.message}
              maxLength={INVITEE_NAME_MAX_LENGTH}
              {...field}
            />
          )}
        />
      </div>

      <div className={styles.role}>
        <h3 className={styles.title}>Choose role</h3>
        <ul className={styles.options}>
          {roleOptions.map((roleOption, index) => (
            <li key={roleOption.value}>
              <Controller
                name="role"
                control={form.control}
                render={({ field }) => (
                  <RadioButton
                    id={`role_option_${index}`}
                    label={roleOption.label}
                    defaultChecked={roleOption.value === defaultRole}
                    {...field}
                    value={roleOption.value}
                  />
                )}
              />
            </li>
          ))}
        </ul>
      </div>

      <button type="submit" disabled={form.formState.isSubmitting}>
        <CopyIcon />
        <span>Get invitation link</span>
      </button>
    </form>
  );
};

export default GenerateLink;
